<template>
  <div>
    <Notification
      id="review-link-toast"
      class="mt-16"
      :message="toastMessage"
      :trigger-notification="showToast"
      @toggle-visibility="showToast = false"
    />
    <Popup
      :model-value="modelValue"
      popup-header="How do you want to request a review?"
      popup-size="lg"
      header-class="text-h2"
      @close="$emit('close')"
    >
      <p class="text-body text-grey-body mb-16">
        Select a method and Sittercity will help you send a one-time review link to anyone.
      </p>
      <Button
        value="Share external review link"
        variant="outlined"
        class="w-full mb-16"
        size="medium"
        :loading="isCopyingExternalReviewLink"
        :disabled="isCopyingExternalReviewLink"
        @click="getOneTimeUseLink"
        ><template #left-icon><share-icon class="mr-8" :width="16" :height="16" /></template
      ></Button>
      <Button value="Request on sittercity" variant="outlined" class="w-full" size="medium" :to="{ name: 'RequestTabs' }" />
    </Popup>
  </div>
</template>

<script lang="ts">
import Notification from '@/components/notification/Notification.vue';
import Popup from '@/components/popup/Popup.vue';
import Button from '@/components/button/Button.vue';
import ShareIcon from '@/graphics/icons/ShareIcon.vue';
import { CREATE_EXTERNAL_REVIEW_REQUEST_MUTATION } from '@/graphql/ratings/mutations';
import { sendSegmentEvent } from '@/lib/segment';

export default {
  name: 'RequestReviewPopup',
  components: {
    Notification,
    Popup,
    Button,
    ShareIcon,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    sitterId: {
      type: String,
      required: true,
    },
    sitterName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      toastMessage: '',
      showToast: false,
      isCopyingExternalReviewLink: false,
    };
  },
  methods: {
    showCopiedNotification() {
      this.toastMessage = 'Review link copied. Paste it into a text or email to share. Each link can be used for one review.';
      this.showToast = true;
      this.$emit('close');
      setTimeout(() => {
        const toastElement = document.getElementById('review-link-toast');
        if (toastElement) {
          toastElement.focus();
        }
        document.body.scrollTop = document.documentElement.scrollTop = 0;
      }, 1);
    },
    showCopyError() {
      this.toastMessage = 'Error copying review link. Please try again.';
      this.showToast = true;
    },
    async getOneTimeUseLink() {
      if (this.isCopyingExternalReviewLink) return;

      this.isCopyingExternalReviewLink = true;
      try {
        const payload = await this.$apollo.mutate({
          mutation: CREATE_EXTERNAL_REVIEW_REQUEST_MUTATION,
          variables: {},
        });
        const reviewToken = payload.data.externalProviderReviewRequests.create.reviewToken;
        const oneTimeUseLink = `${window.location.origin}/ratings/sitters/${this.sitterId}/new_external/${reviewToken}`;
        await navigator.clipboard.writeText(oneTimeUseLink);
        this.showCopiedNotification();
        sendSegmentEvent('Sitter External Review Request Started', {
          sitter_uuid: this.sitterId,
          review_id: reviewToken,
        });
      } catch (err) {
        console.error('Error copying external review link:', err);
        this.showCopyError();
      } finally {
        this.isCopyingExternalReviewLink = false;
      }
    },
  },
};
</script>
